/**
 * Warranty Master Document admin — upload + activate revisions of the
 * unified Commissioning + PM + Warranty acceptance document.
 *
 * Route: /admin/warranty/master-doc
 *
 * Three columns:
 *   • Left: version list (active highlighted, supersede status, upload form)
 *   • Center: section editor for the selected/draft version
 *   • Right: live compliance probe (commissioning + PM + warranty present?)
 *
 * Compliance gate: the backend refuses uploads that don't mention all 3
 * processes; this UI runs the probe live so admins fix issues before saving.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, FileText, Wrench, DownloadSimple, Copy, Check, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api from "@/lib/api";
import { useAuthenticatedDownload } from "@/lib/useAuthenticatedDownload";
import AccountMenu from "@/components/AccountMenu";
import { useT } from "@/lib/i18n";
import {
  groupMasterDocSections,
  policyGroupCounts,
} from "@/lib/masterDocGrouping";

const ROLES = ["admin", "dealer", "customer"];

export default function WarrantyMasterDocPage({ user, onLogout, onLogoutAll }) {
  const navigate = useNavigate();
  const t = useT();
  const [active, setActive] = useState(null);     // current active version (DB or seed)
  const [versions, setVersions] = useState([]);   // all uploaded
  const [editing, setEditing] = useState(null);   // draft being edited
  const [compliance, setCompliance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  // iter150AZ.60 — authenticated downloads for the "Active" PDF + the
  // "Unsigned reference copy" link below the header.
  const { downloading: unsignedRefDownloading, download: downloadUnsignedRef } =
    useAuthenticatedDownload();
  const [publicLink, setPublicLink] = useState(null);  // {url, token, version}
  const [linkCopied, setLinkCopied] = useState(false);

  // iter114 — self-serve "email this policy to me" button. Hits a
  // throttled endpoint (point_id `warranty_policy_self`) which
  // attaches the cached PDF and ships it to the requesting user's
  // email. No recipient picker needed — recipient is always the
  // signed-in user.
  const emailToMe = useCallback(async () => {
    if (emailBusy) return;
    setEmailBusy(true);
    try {
      const r = await api.post(`/warranty/policy/email-to-me`);
      const status = (r.data || {}).status;
      const to = (r.data || {}).to;
      if (status === "delivered" || status === "mocked") {
        toast.success(t("wmd.email_to_me_ok", `Sent — check ${to}`));
      } else if (status === "throttled") {
        toast.warning(t("wmd.email_to_me_throttled", "Daily cap reached — try again tomorrow."));
      } else {
        toast.error(t("wmd.email_to_me_err",
          `Send failed: ${(r.data || {}).message || status}`));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setEmailBusy(false);
    }
  }, [emailBusy, t]);

  const loadPublicLink = useCallback(async () => {
    try {
      const r = await api.get("/warranty/policy/public-link");
      setPublicLink(r.data);
    } catch { /* silent — admins simply won't see the badge */ }
  }, []);

  const copyPublicLink = async () => {
    if (!publicLink?.url) return;
    try {
      await navigator.clipboard.writeText(publicLink.url);
      setLinkCopied(true);
      toast.success(t("wmd.public_link_copied", "Public link copied to clipboard"));
      setTimeout(() => setLinkCopied(false), 2500);
    } catch {
      toast.error(t("wmd.public_link_copy_failed", "Could not copy — select the URL manually."));
    }
  };

  // iter114 (improvement) — single-click "share with stakeholder mid-call"
  // flow: ensure the public link is loaded (fetch on demand if it wasn't
  // already), copy to clipboard, toast. Saves the user from scrolling
  // past the version list to find the public-link badge.
  const [shareBusy, setShareBusy] = useState(false);
  // Iter114 (token-reissue badge) — when the active doc version changes
  // the public-link token rotates server-side, but our cached
  // `publicLink` may still hold the old one. This flag is true when the
  // currently-loaded public link's `version` doesn't match the active
  // doc; `quickShare` will re-fetch automatically before copying so the
  // user always pastes a fresh URL.
  const activeVersionString = active?.version?.version || null;
  const linkIsStale = !!(publicLink?.version
    && activeVersionString
    && publicLink.version !== activeVersionString);
  const quickShare = async () => {
    if (shareBusy) return;
    setShareBusy(true);
    try {
      let url = publicLink?.url;
      // Re-fetch when stale OR when missing — guarantees the user
      // never copies a now-broken token.
      if (!url || linkIsStale) {
        const r = await api.get("/warranty/policy/public-link");
        setPublicLink(r.data);
        url = r.data?.url;
      }
      if (!url) throw new Error("No public link available");
      await navigator.clipboard.writeText(url);
      toast.success(t("wmd.quick_share_copied", "Public Warranty Policy URL copied — paste into chat / email / SMS."));
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("wmd.quick_share_failed", "Could not copy share link."));
    } finally {
      setShareBusy(false);
    }
  };

  const { downloading: fullPdfDl, download: dlFullPdf } = useAuthenticatedDownload();
  const downloadFullPdf = () => {
    // iter150AZ.60 — Previously this created a raw ``<a download>`` and
    // clicked it. The browser session cookie sometimes carried the
    // auth, but in localStorage-only sessions (PWA install on iOS,
    // Emergent preview iframe) the request reached the backend
    // unauthenticated and the user got a blank/403 page. Route the
    // download through ``api`` (which attaches X-User-Email) and
    // trigger a client-side download from the resulting Blob.
    const versionLabel = active?.version?.version || "warranty-policy";
    const safeVersion = String(versionLabel).replace(/[^A-Za-z0-9._-]/g, "_");
    dlFullPdf(
      "/warranty/master-doc.pdf",
      `warranty_policy_${safeVersion}.pdf`,
      { params: { download: 1 }, successMessage: t("wmd.download_started", "Download started") },
    );
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const [a, v] = await Promise.all([
        api.get("/admin/warranty/master-doc"),
        api.get("/admin/warranty/master-doc/versions"),
      ]);
      setActive(a.data);
      setVersions(v.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load Warranty Policy");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); loadPublicLink(); }, [loadPublicLink]);

  // ─── Live compliance probe (debounced) ───────────────────────
  useEffect(() => {
    if (!editing) { setCompliance(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.post("/admin/warranty/master-doc/preview-compliance",
          { sections: editing.sections.map(s => ({ ...s, role_visibility: s.role_visibility?.length ? s.role_visibility : ROLES })) });
        setCompliance(r.data);
      } catch (e) {/* nonfatal */}
    }, 350);
    return () => clearTimeout(t);
  }, [editing]);

  const startNewDraft = () => {
    if (!active) return;
    const seed = active.version || active;
    setEditing({
      version: bumpVersion(seed.version),
      revision_date: new Date().toISOString().slice(0, 10),
      revision_note: "",
      activate: true,
      sections: (seed.sections || []).map(s => ({
        key: s.key, title: s.title,
        role_visibility: [...(s.role_visibility || ROLES)],
        body_md: s.body_md, signature_clause: !!s.signature_clause,
      })),
    });
  };

  const updateSection = (idx, patch) => {
    setEditing(e => {
      const sections = [...e.sections];
      sections[idx] = { ...sections[idx], ...patch };
      return { ...e, sections };
    });
  };
  const removeSection = (idx) => {
    setEditing(e => ({ ...e, sections: e.sections.filter((_, i) => i !== idx) }));
  };
  const addSection = () => {
    setEditing(e => ({
      ...e,
      sections: [
        ...e.sections,
        { key: "new_section_" + (e.sections.length + 1), title: "New section",
          role_visibility: [...ROLES], body_md: "", signature_clause: false },
      ],
    }));
  };

  const upload = async () => {
    if (!editing) return;
    setUploading(true);
    try {
      const r = await api.post("/admin/warranty/master-doc/versions", editing);
      toast.success(`Uploaded ${r.data.version} (${r.data.status})`);
      setEditing(null);
      await refresh();
      // Auto-trigger PDF download for the freshly-uploaded ACTIVE version
      // — the backend pre-bakes it during the same request, so by the time
      // refresh() resolves the Mongo cache is warm. Skips for drafts since
      // those don't update the active master-doc.
      if (r.data.status === "active") {
        // Slight delay so the Sonner toast renders before the browser save
        // dialog steals focus.
        setTimeout(() => downloadFullPdf(), 400);
        loadPublicLink();  // token rotates with version
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "COMPLIANCE_FAIL") {
        toast.error(detail.message);
        setCompliance(detail.compliance);
      } else {
        toast.error(typeof detail === "string" ? detail : "Upload failed");
      }
    } finally {
      setUploading(false);
    }
  };

  const activate = async (vid) => {
    try {
      await api.post(`/admin/warranty/master-doc/versions/${vid}/activate`);
      toast.success("Version activated");
      await refresh();
      // Refresh the pre-baked PDF cache + auto-download the freshly active doc.
      try {
        await api.post("/admin/warranty/master-doc/pdf-cache/rebuild").catch(() => null);
      } catch { /* ignore — frontend-driven activation already triggered server-side rebuild */ }
      setTimeout(() => downloadFullPdf(), 400);
      loadPublicLink();  // token rotates with version
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Activate failed");
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-slate-200 px-4 md:px-8 h-14 flex items-center gap-3 max-w-7xl mx-auto">
        <button data-testid="masterdoc-back-home" onClick={() => navigate("/")}
          className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500"
          title="Home">
          <ArrowLeft size={18} weight="bold" />
        </button>
        <FileText size={20} weight="fill" className="text-blue-300" />
        <h1 className="text-base font-black tracking-tighter font-['Chivo']" data-testid="masterdoc-title">
          {t("wmd.title", "Warranty Policy")}
        </h1>
        <button
          onClick={downloadFullPdf}
          disabled={fullPdfDl}
          data-testid="masterdoc-download-pdf"
          title={t("wmd.download_pdf_hint", "Download the full Warranty Policy as a PDF, including the Labor Coverage Policy section.")}
          className="ml-3 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-violet-700 border border-violet-300 hover:bg-violet-700 hover:text-white px-3 py-1.5 rounded-sm bg-violet-50/40 disabled:opacity-40"
        >
          <DownloadSimple size={13} weight="fill" />
          {fullPdfDl ? t("wmd.downloading", "Downloading…") : t("wmd.download_pdf", "Download PDF")}
        </button>
        {/* iter150W — "unsigned reference copy" link, relocated from the
            main landing page so it lives alongside the active-version
            download. Same backend endpoint (/api/warranty/master-doc.pdf)
            — role-filtered server-side. Subtle styling so it sits as a
            secondary action next to the primary "Download PDF" button.
            iter150AZ.60 — was an ``<a target="_blank">`` that dropped
            X-User-Email; now uses the authenticated-download hook. */}
        <button
          type="button"
          data-testid="masterdoc-unsigned-reference-download"
          disabled={unsignedRefDownloading}
          onClick={() => downloadUnsignedRef(
            "/warranty/master-doc.pdf",
            "Warranty_Policy_unsigned_reference.pdf",
          )}
          title={t("wmd.unsigned_reference_hint", "Download the current warranty document — unsigned reference copy for review.")}
          className="ml-2 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 underline underline-offset-2 disabled:opacity-60 disabled:cursor-wait bg-transparent border-0"
        >
          {unsignedRefDownloading
            ? t("wmd.preparing_pdf", "Preparing…")
            : t("wmd.unsigned_reference_download", "Unsigned reference copy")}
        </button>
        <button
          onClick={emailToMe}
          disabled={emailBusy}
          data-testid="masterdoc-email-to-me"
          title={t("wmd.email_to_me_hint", "Email yourself a copy of the active Warranty Policy PDF.")}
          className="ml-2 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-blue-700 border border-blue-300 hover:bg-blue-700 hover:text-white px-3 py-1.5 rounded-sm bg-blue-50/40 disabled:opacity-40"
        >
          <EnvelopeSimple size={13} weight="fill" />
          {emailBusy
            ? t("wmd.emailing_to_me", "Sending…")
            : t("wmd.email_to_me", "Email this to me")}
        </button>
        {/* iter114 improvement — one-click "copy public URL" so admins
            can forward the warranty policy link to a stakeholder
            mid-call without scrolling to the version list. Fetches
            the link on demand if not already loaded. */}
        <button
          onClick={quickShare}
          disabled={shareBusy}
          data-testid="masterdoc-quick-share"
          title={linkIsStale
            ? t("wmd.quick_share_stale_hint",
                "Active doc version changed — the cached share token is stale. Click to mint and copy a fresh URL.")
            : t("wmd.quick_share_hint", "Copy the public Warranty Policy URL to your clipboard.")}
          className={`ml-2 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm disabled:opacity-40 ${linkIsStale ? "text-amber-800 border border-amber-300 hover:bg-amber-700 hover:text-white bg-amber-50/40" : "text-emerald-700 border border-emerald-300 hover:bg-emerald-700 hover:text-white bg-emerald-50/40"}`}
        >
          {linkCopied ? <Check size={13} weight="bold" /> : <Copy size={13} weight="fill" />}
          {shareBusy
            ? t("wmd.quick_share_busy", "Copying…")
            : linkCopied
              ? t("wmd.quick_share_done", "Copied!")
              : linkIsStale
                ? t("wmd.quick_share_stale", "Quick share (token reissued)")
                : t("wmd.quick_share", "Quick share")}
        </button>
        <div className="ml-auto"><AccountMenu user={user} onLogout={onLogout} onLogoutAll={onLogoutAll} /></div>
      </header>
      <main className="max-w-7xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* ─── Left: versions ───────────────────────────────── */}
        <aside className="lg:col-span-3 space-y-4">
          <div className="bg-white border rounded-md p-4">
            <h2 className="font-bold text-sm uppercase tracking-wider text-slate-700 mb-2">Active</h2>
            {loading ? <div className="text-xs text-slate-500">Loading…</div> : active && (
              <div data-testid="masterdoc-active" className="text-sm">
                <div className="font-mono font-bold text-blue-700">{active.version?.version}</div>
                <div className="text-xs text-slate-500">{active.source === "db" ? "Custom" : "Seed (built-in)"}</div>
                <div className="text-[10px] text-slate-400">{active.version?.sections?.length} sections</div>
                {active.version?.compliance && <ComplianceBadge c={active.version.compliance} />}
              </div>
            )}
          </div>
          {/* Public dealer-share link — rotates token on every doc activation. */}
          {publicLink?.url && (
            <div className="bg-violet-50 border border-violet-200 rounded-md p-4 space-y-2"
                 data-testid="masterdoc-public-link">
              <h2 className="font-bold text-[11px] uppercase tracking-wider text-violet-800">
                {t("wmd.public_link_label", "Dealer-share link")}
              </h2>
              <p className="text-[10px] text-violet-700 leading-snug italic">
                {t("wmd.public_link_hint", "Paste in dealer email signatures or websites. Token rotates on every doc activation.")}
              </p>
              <div className="flex gap-1">
                <input
                  readOnly
                  value={publicLink.url}
                  data-testid="masterdoc-public-link-input"
                  onFocus={(e) => e.target.select()}
                  className="flex-1 text-[10px] font-mono px-2 py-1.5 border border-violet-300 rounded-sm bg-white"
                />
                <button
                  onClick={copyPublicLink}
                  data-testid="masterdoc-public-link-copy"
                  className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-violet-700 border border-violet-300 hover:bg-violet-700 hover:text-white px-2 py-1.5 rounded-sm bg-violet-50/40"
                  title={t("wmd.public_link_copy", "Copy link")}
                >
                  {linkCopied
                    ? <><Check size={11} weight="bold" /> {t("wmd.public_link_copied_short", "Copied")}</>
                    : <><Copy size={11} weight="bold" /> {t("wmd.public_link_copy", "Copy link")}</>}
                </button>
              </div>
              <div className="text-[10px] text-violet-600 font-mono">
                {t("wmd.public_link_token", "Token")}: {publicLink.token}
              </div>
            </div>
          )}
          <div className="bg-white border rounded-md p-4">
            <h2 className="font-bold text-sm uppercase tracking-wider text-slate-700 mb-2">{t("wmd.all_versions", "All versions")}</h2>
            <ul data-testid="masterdoc-version-list" className="space-y-2 text-xs">
              {versions.length === 0 && <li className="text-slate-500">{t("wmd.no_custom_versions", "No custom versions yet.")}</li>}
              {versions.map(v => (
                <li key={v.id} className="flex items-center justify-between">
                  <span className="font-mono">{v.version}</span>
                  {v.status === "active" ? <span className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded text-[10px] font-bold">ACTIVE</span> :
                   v.status === "superseded" ? <span className="bg-slate-100 text-slate-500 px-2 py-0.5 rounded text-[10px]">superseded</span> :
                   <button data-testid={`masterdoc-activate-${v.id}`} onClick={() => activate(v.id)} className="text-[10px] text-blue-600 hover:text-blue-800 font-bold">Activate</button>}
                </li>
              ))}
            </ul>
          </div>
          <button data-testid="masterdoc-start-draft" onClick={startNewDraft} disabled={!active}
            className="w-full bg-violet-600 text-white py-2 rounded font-bold text-sm hover:bg-violet-700 disabled:opacity-50">
            {t("wmd.start_new_revision", "Start new revision")}
          </button>
        </aside>

        {/* ─── Center: editor ───────────────────────────────── */}
        <section className="lg:col-span-6 space-y-3">
          {!editing && (
            <ActiveDocPreview active={active} />
          )}
          {editing && (
            <>
              <div className="bg-white border rounded-md p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="Version" value={editing.version} onChange={v => setEditing({ ...editing, version: v })} testid="masterdoc-version-input" />
                <Field label={t("wmd.revision_date", "Revision date")} value={editing.revision_date} onChange={v => setEditing({ ...editing, revision_date: v })} testid="masterdoc-date-input" />
                <label className="text-xs space-y-1">
                  <span className="block uppercase tracking-wider font-bold text-slate-600">{t("wmd.activate_immediately", "Activate immediately")}</span>
                  <input type="checkbox" data-testid="masterdoc-activate-checkbox"
                    checked={editing.activate}
                    onChange={e => setEditing({ ...editing, activate: e.target.checked })} />
                </label>
                <div className="md:col-span-3">
                  <label className="text-xs uppercase tracking-wider font-bold text-slate-600 block mb-1">{t("wmd.revision_notes", "Revision notes")}</label>
                  <textarea data-testid="masterdoc-note-input"
                    value={editing.revision_note}
                    onChange={e => setEditing({ ...editing, revision_note: e.target.value })}
                    rows={2} className="w-full border rounded px-2 py-1 text-sm" />
                </div>
              </div>
              {editing.sections.map((s, i) => (
                <SectionEditor key={i} section={s} idx={i}
                  onChange={(patch) => updateSection(i, patch)}
                  onRemove={() => removeSection(i)} />
              ))}
              <button data-testid="masterdoc-add-section" onClick={addSection}
                className="text-sm font-bold text-violet-700 hover:text-violet-900">+ {t("wmd.add_section", "Add section")}</button>
              <div className="flex justify-end gap-2 sticky bottom-0 bg-slate-50 py-3">
                <button onClick={() => setEditing(null)} className="px-3 py-2 text-sm border rounded">{t("action.cancel", "Cancel")}</button>
                <button data-testid="masterdoc-save-draft" onClick={upload} disabled={uploading}
                  className="px-4 py-2 bg-violet-600 text-white rounded font-bold text-sm hover:bg-violet-700 disabled:opacity-50">
                  {uploading ? "Uploading…" : (editing.activate ? "Save & activate" : "Save as draft")}
                </button>
              </div>
            </>
          )}
        </section>

        {/* ─── Right: live compliance probe ──────────────────── */}
        <aside className="lg:col-span-3 space-y-3">
          <div className="bg-white border rounded-md p-4 sticky top-4">
            <h2 className="font-bold text-sm uppercase tracking-wider text-slate-700 mb-2">{t("wmd.compliance_check", "Compliance check")}</h2>
            <p className="text-xs text-slate-500 mb-3">{t("wmd.compliance_hint", "The Warranty Policy must mention all 3 processes.")}</p>
            <div className="space-y-2 text-sm">
              <ComplianceLine label="Commissioning" ok={compliance?.commissioning_present ?? active?.version?.compliance?.commissioning_present} />
              <ComplianceLine label={t("wmd.pm_service", "PM service")} ok={compliance?.pm_present ?? active?.version?.compliance?.pm_present} />
              <ComplianceLine label={t("wmd.warranty_claim", "Warranty claim")} ok={compliance?.warranty_present ?? active?.version?.compliance?.warranty_present} />
            </div>
            {compliance && !compliance.ok && (
              <div data-testid="masterdoc-compliance-fail"
                className="mt-3 p-3 bg-red-50 border-l-4 border-red-400 rounded text-xs text-red-800">
                Missing: {compliance.missing.join(", ")}{t("wmd.upload_rejected", ". The upload will be rejected until all 3 are present.")}
              </div>
            )}
          </div>
        </aside>
      </main>
    </div>
  );
}

function bumpVersion(v) {
  if (!v) return "v1.0.0";
  const m = v.match(/^v?(\d+)\.(\d+)\.(\d+)/);
  if (!m) return `${v}-rev`;
  const [_, a, b, c] = m;
  return `v${a}.${b}.${Number(c) + 1}`;
}

function ComplianceBadge({ c }) {
  if (!c) return null;
  const ok = c.ok;
  return (
    <span data-testid="masterdoc-compliance-badge" className={`mt-1 inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded ${ok ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>
      {ok ? "Compliant" : "Non-compliant"}
    </span>
  );
}

function ComplianceLine({ label, ok }) {
  const tone = ok === true ? "text-emerald-700" : ok === false ? "text-red-700" : "text-slate-500";
  const icon = ok === true ? "✓" : ok === false ? "✗" : "·";
  return <div className={`flex items-center gap-2 ${tone}`}><span className="font-bold">{icon}</span>{label}</div>;
}

function Field({ label, value, onChange, testid }) {
  return (
    <label className="text-xs space-y-1">
      <span className="block uppercase tracking-wider font-bold text-slate-600">{label}</span>
      <input data-testid={testid} value={value || ""} onChange={e => onChange(e.target.value)}
        className="w-full border rounded px-2 py-1 text-sm" />
    </label>
  );
}

function SectionEditor({ section, idx, onChange, onRemove }) {
  const t = useT();
  return (
    <div data-testid={`masterdoc-section-${idx}`} className="bg-white border rounded-md p-4 space-y-2">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <Field label="Key" value={section.key} onChange={v => onChange({ key: v })} testid={`masterdoc-section-${idx}-key`} />
        <Field label="Title" value={section.title} onChange={v => onChange({ title: v })} testid={`masterdoc-section-${idx}-title`} />
      </div>
      <div>
        <span className="text-xs uppercase tracking-wider font-bold text-slate-600">{t("wmd.visible_to", "Visible to")}</span>
        <div className="flex gap-3 mt-1 text-xs">
          {ROLES.map(r => (
            <label key={r} className="inline-flex items-center gap-1">
              <input type="checkbox" data-testid={`masterdoc-section-${idx}-role-${r}`}
                checked={(section.role_visibility || []).includes(r)}
                onChange={e => onChange({
                  role_visibility: e.target.checked
                    ? [...new Set([...(section.role_visibility || []), r])]
                    : (section.role_visibility || []).filter(x => x !== r),
                })} />
              {r}
            </label>
          ))}
        </div>
      </div>
      <label className="inline-flex items-center gap-2 text-xs">
        <input type="checkbox" data-testid={`masterdoc-section-${idx}-sigclause`}
          checked={!!section.signature_clause}
          onChange={e => onChange({ signature_clause: e.target.checked })} />
        Signature clause (signer must accept this)
      </label>
      <textarea data-testid={`masterdoc-section-${idx}-body`}
        value={section.body_md || ""}
        onChange={e => onChange({ body_md: e.target.value })}
        rows={6} className="w-full border rounded px-2 py-1 text-sm font-mono"
        placeholder={t("wmd.markdown_body", "Markdown body…")} />
      {section.body_md ? (
        <details className="border rounded bg-slate-50 px-3 py-2" data-testid={`masterdoc-section-${idx}-preview-details`}>
          <summary className="cursor-pointer text-[11px] font-bold uppercase tracking-wider text-slate-600">
            {t("wmd.live_preview", "Live preview")}
          </summary>
          <div className="mt-2" data-testid={`masterdoc-section-${idx}-preview`}>
            <MarkdownBody text={section.body_md} />
          </div>
        </details>
      ) : null}
      <div className="flex justify-end">
        <button data-testid={`masterdoc-section-${idx}-remove`} onClick={onRemove}
          className="text-xs text-red-600 hover:text-red-800">{t("wmd.remove_section", "Remove section")}</button>
      </div>
    </div>
  );
}

function ActiveDocPreview({ active }) {
  const t = useT();
  const v = active?.version;
  if (!v) return null;
  const sections = v.sections || [];
  const grouped = groupMasterDocSections(sections);
  const counts = policyGroupCounts(sections);
  const laborCount = counts.labor_coverage || 0;
  return (
    <div data-testid="masterdoc-active-preview" className="bg-white border rounded-md p-4">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <h2 className="text-lg font-bold font-['Chivo']">{v.version}</h2>
        <span className="text-xs text-slate-500">{v.revision_date}</span>
        {active?.source === "seed" && <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-[10px] font-bold">SEED</span>}
        {laborCount > 0 && (
          <span
            data-testid="masterdoc-labor-coverage-badge"
            className="inline-flex items-center gap-1 bg-violet-100 text-violet-800 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider"
            title={t("wmd.labor_coverage_policy_summary",
              "When warranty labor is covered, who can perform it, the 24-month dealer-only window, and the dealer-account-credit reimbursement model.")}
          >
            <Wrench size={11} weight="fill" />
            {t("wmd.labor_coverage_policy", "Labor Coverage Policy")} · {laborCount}
          </span>
        )}
      </div>
      {v.revision_note && <p className="text-xs text-slate-500 mb-3 italic">{v.revision_note}</p>}
      <div className="space-y-3 max-h-[600px] overflow-y-auto" data-testid="masterdoc-preview-list">
        {grouped.map((block, idx) => {
          if (block.kind === "section") {
            return <DocSectionPreview key={`s-${idx}-${block.section.key}`} section={block.section} />;
          }
          // policy_group block — render banner + indented children.
          return (
            <PolicyGroupBlock
              key={`g-${idx}-${block.group.id}`}
              group={block.group}
              sections={block.sections}
              t={t}
            />
          );
        })}
      </div>
    </div>
  );
}

function DocSectionPreview({ section: s }) {
  return (
    <details className="border rounded p-2">
      <summary className="cursor-pointer font-bold text-sm flex items-center gap-2">
        {s.title}
        <span className="text-[10px] uppercase tracking-wider text-slate-400">{(s.role_visibility || []).join(" · ")}</span>
        {s.signature_clause && <span className="text-[10px] bg-amber-100 text-amber-800 px-1.5 rounded">SIGN</span>}
      </summary>
      <pre className="hidden">{s.body_md}</pre>
      <div className="mt-2" data-testid={`masterdoc-preview-${s.key}`}>
        <MarkdownBody text={s.body_md} />
      </div>
    </details>
  );
}

/**
 * Visible policy-group banner that wraps consecutive sections sharing a
 * key prefix (e.g. ``labor_coverage_*``). Renders an expandable header so
 * the admin / commissioning reader can scan the heading and dive in for
 * the 15-section breakdown only when they need it. The banner is itself
 * a `<details open>` so by default the labor-coverage sections are
 * visible — keeps the policy "easily seen" per the user requirement.
 */
function PolicyGroupBlock({ group, sections, t }) {
  const tone = group.accent === "violet"
    ? "border-violet-300 bg-violet-50/50"
    : "border-slate-300 bg-slate-50/50";
  const headerTone = group.accent === "violet"
    ? "text-violet-900"
    : "text-slate-900";
  return (
    <details
      open
      className={`border-l-4 ${tone} rounded-sm pl-3 pr-2 py-2`}
      data-testid={`masterdoc-policy-group-${group.id}`}
    >
      <summary className="cursor-pointer flex items-center gap-2 flex-wrap">
        <Wrench size={14} weight="fill" className="text-violet-600" />
        <span className={`text-sm font-black uppercase tracking-wider ${headerTone}`}>
          {t(group.titleKey, group.titleFallback)}
        </span>
        <span className="text-[10px] font-bold uppercase tracking-wider text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded">
          {sections.length} {t("wmd.sections_short", "sections")}
        </span>
      </summary>
      <p className="text-xs italic text-slate-600 mt-1 mb-2 leading-relaxed">
        {t(group.summaryKey, group.summaryFallback)}
      </p>
      <div className="space-y-2 pl-2 border-l border-violet-200">
        {sections.map((s) => (
          <DocSectionPreview key={s.key} section={s} />
        ))}
      </div>
    </details>
  );
}

/**
 * Lightweight Markdown renderer that styles the document body WITHOUT
 * the @tailwindcss/typography plugin (we don't ship it). Handles the
 * subset that the master doc uses: **bold**, numbered lists, bullet
 * lists, paragraphs, headings (h3+), inline code.
 *
 * Preprocess: the master document source uses the Unicode bullet "•"
 * because the PDF renderer writes them verbatim. Markdown treats "•"
 * as plain text, so "• foo\n• bar" collapses into one paragraph with
 * line-wrapped runs. We rewrite lines that start with "• " (optionally
 * preceded by whitespace) into "- " so ReactMarkdown builds a proper
 * <ul>. Numbered lists ("1. ", "2. "…) already use Markdown syntax so
 * they render fine without a rewrite.
 */
function normalizeBullets(text) {
  if (!text) return text;
  return text.replace(/(^|\n)(\s*)•\s+/g, "$1$2- ");
}

function MarkdownBody({ text }) {
  if (!text) return null;
  const normalized = normalizeBullets(text);
  return (
    <div className="text-[13px] leading-relaxed text-slate-700 space-y-2">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ node, ...props }) => <p className="my-2" {...props} />,
          strong: ({ node, ...props }) => (
            <strong className="font-bold text-slate-900" {...props} />
          ),
          em: ({ node, ...props }) => <em className="italic" {...props} />,
          ul: ({ node, ...props }) => (
            <ul className="list-disc pl-5 space-y-1 my-2" {...props} />
          ),
          ol: ({ node, ...props }) => (
            <ol className="list-decimal pl-5 space-y-1 my-2" {...props} />
          ),
          li: ({ node, ...props }) => <li className="leading-snug" {...props} />,
          h1: ({ node, ...props }) => (
            <h1 className="text-lg font-black tracking-tight mt-4 mb-2" {...props} />
          ),
          h2: ({ node, ...props }) => (
            <h2 className="text-base font-bold mt-3 mb-2" {...props} />
          ),
          h3: ({ node, ...props }) => (
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-600 mt-3 mb-1" {...props} />
          ),
          code: ({ node, inline, ...props }) =>
            inline ? (
              <code className="bg-slate-100 text-slate-800 rounded px-1 py-0.5 font-mono text-[11px]" {...props} />
            ) : (
              <code className="block bg-slate-100 rounded p-2 font-mono text-[11px] overflow-x-auto" {...props} />
            ),
          a: ({ node, ...props }) => (
            <a className="text-blue-700 underline hover:text-blue-900" target="_blank" rel="noopener noreferrer" {...props} />
          ),
          hr: () => <hr className="my-3 border-slate-200" />,
        }}
      >
        {normalized}
      </ReactMarkdown>
    </div>
  );
}
